<template>
  <div ref="heroEl" class="mx-px">
    <ClientOnly>
      <div class="grid" :class="gridPatternClasses">
        <PatternBase
          v-for="i in patternTotalNumber"
          :key="i"
          class="-m-px stroke-2 shadow-pattern shadow-current"
          :class="colorClass"
        />
        <div
          v-if="props.iconForType"
          class="order-first col-span-1 row-span-1 -m-px flex items-center justify-center bg-primary-400 stroke-2 text-primary-400 shadow-pattern shadow-current sm:hidden"
        >
          <slot name="typeIcon">
            <BasicIcon
              :type="props.iconForType"
              class="h-8 w-8 text-shade-50"
            />
          </slot>
        </div>
        <div
          v-if="$slots.default"
          ref="grid"
          class="col-span-full row-span-3 -m-px p-px shadow-pattern shadow-current"
          :class="[
            size === 'large' ? 'row-start-2' : 'sm:row-start-1',
            { 'hidden md:block': size === 'smaller' },

            colorClass,
            patternWidthNumber === 6 ? 'sm:col-span-6' : 'sm:col-span-7',
          ]"
          :style="`grid-column-start: ${colStart}`"
        >
          <div class="flex h-full items-center text-shade-900">
            <div
              v-if="props.iconForType"
              class="m-14 hidden h-40 w-40 shrink-0 items-center justify-center sm:flex lg:h-60 lg:w-60"
            >
              <slot name="typeIcon">
                <div class="p-10">
                  <BasicIcon
                    class="h-20 w-20 flex-auto shrink-0 text-shade-50"
                    :type="props.iconForType"
                  />
                </div>
              </slot>
            </div>
            <slot></slot>
          </div>
        </div>
      </div>
      <template #fallback>
        <div
          :class="[
            'w-full animate-pulse bg-shade-100',
            size === 'large' ? 'h-[58vh]' : '',
            size === 'medium' ? 'h-[38vh]' : '',
            size === 'small' ? 'h-[18vh]' : '',
            size === 'smaller' ? 'h-[12vh]' : '',
          ]"
        />
      </template>
    </ClientOnly>
  </div>
</template>
<script lang="ts" setup>
import { breakpointsTailwind } from '@vueuse/core'
import { PropType } from 'vue'

const slots = useSlots()

const props = defineProps({
  size: {
    type: String as PropType<HeroSize>,
    default: 'large',
  },
  colorClass: {
    type: String,
    required: true,
  },
  customPatternHeight: {
    type: Number,
    default: null,
  },
  forcePatternSize: {
    type: String as PropType<HeroSize>,
    default: null,
  },
  /**
   * Adds an icon corresponding the the resource in the header of the hero
   */
  iconForType: {
    type: String as PropType<ResourceType>,
    default: null,
  },
})
const grid = ref()
const breakpoints = useBreakpoints(breakpointsTailwind)
const lgAndLarger = breakpoints.greater('lg')
const smAndLower = breakpoints.smaller('sm')
const medium = breakpoints.greater('sm')

const patternSize = computed<HeroSize>(() => {
  if (props.forcePatternSize) {
    return props.forcePatternSize
  }
  if (lgAndLarger.value) return 'large'
  if (medium.value) return 'medium'
  return 'small'
})

const patternPreferredWidth = computed(() => {
  switch (patternSize.value) {
    case 'large':
      return 120
    case 'medium':
      return 90
    default:
      return 72
  }
})
const heroEl = ref(null)
const { width, height } = useElementSize(heroEl)
const patternWidthNumber = computed(() => {
  if (!width || width.value === Infinity) return 0
  return Math.floor(width.value / patternPreferredWidth.value)
})
const patternHeightNumber = computed(() => {
  if (props.customPatternHeight) {
    return props.customPatternHeight
  }
  let largeSize = 5
  let mediumSize = 3
  let smallSize = 1
  if (smAndLower.value) {
    largeSize = 3
    mediumSize = 2
    smallSize = 2
  }
  switch (props.size) {
    case 'large':
      return largeSize
    case 'medium':
      return mediumSize
    case 'small':
    case 'smaller':
      return smallSize
    default:
      return 5
  }
})
/**
 * The size of the slot as the number of pattern it replaces
 */
const slotSizeAsPatternNumber = computed(() => {
  if (!slots.default) return 0
  if (smAndLower.value) {
    if (props.iconForType) {
      // Add one to the slot size if the icon is shown on sm screens, the icon replaces one pattern
      return patternWidthNumber.value + 1
    }
    return patternWidthNumber.value
  }
  switch (props.size) {
    case 'large':
    case 'medium':
      return Math.min(7, patternWidthNumber.value) * 3
    case 'small':
    case 'smaller':
      return Math.min(7, patternWidthNumber.value)
    default:
      return 0
  }
})
const patternTotalNumber = computed(() => {
  return (
    patternWidthNumber.value * patternHeightNumber.value -
    slotSizeAsPatternNumber.value
  )
})
const gridPatternClasses = computed(() => {
  switch (patternSize.value) {
    case 'large':
      return 'grid-cols-pattern-lg'
    case 'medium':
      return 'grid-cols-pattern-md'
    default:
      return 'grid-cols-pattern-sm'
  }
})
const colStart = computed(() => {
  return Math.max(
    1,
    Math.round((width.value - 1280) / 2 / patternPreferredWidth.value) + 1
  )
})
</script>
